"use client";

import { dynamicChart } from "@/components/charts/dynamicChart";
import type { TreemapRow } from "@/components/charts/Treemap";

const Treemap = dynamicChart(
  () => import("@/components/charts/Treemap").then((m) => m.Treemap),
  420,
);

// Colour-blind-safe sequential: a single-hue dark-green ramp drawn from
// ColorBrewer's "Greens-7" (dark half) plus an extra-dark tail. Single-hue
// sequentials are CB-safe by construction — order is encoded as lightness,
// which deuteranopia / protanopia / tritanopia all preserve. Every stop is
// dark enough for white tile labels. COMPONENTS.md permits inline ramps for
// ordered scales; the family stays adjacent to palette.moss in spirit.
type Rgb = { r: number; g: number; b: number };
const RAMP_STOPS: Rgb[] = [
  { r: 0x31, g: 0xa3, b: 0x54 }, // smallest tiles — ColorBrewer Greens-7 stop 4
  { r: 0x23, g: 0x84, b: 0x43 }, // ColorBrewer Greens-7 stop 5
  { r: 0x00, g: 0x6d, b: 0x2c }, // ColorBrewer Greens-7 stop 6
  { r: 0x00, g: 0x44, b: 0x1b }, // ColorBrewer Greens-7 stop 7
  { r: 0x00, g: 0x22, b: 0x0d }, // biggest tile — extra-dark tail
];

function rampGreen(t: number): string {
  const x = Math.max(0, Math.min(1, t));
  const segments = RAMP_STOPS.length - 1;
  const scaled = x * segments;
  const i = Math.min(Math.floor(scaled), segments - 1);
  const local = scaled - i;
  const a = RAMP_STOPS[i];
  const b = RAMP_STOPS[i + 1];
  return `rgb(${Math.round(a.r + (b.r - a.r) * local)}, ${Math.round(
    a.g + (b.g - a.g) * local,
  )}, ${Math.round(a.b + (b.b - a.b) * local)})`;
}

export type FocusAreaRow = {
  order: number;
  name: string;
  description: string;
};

export type FocusAreaSpendRow = {
  focus_area: string;
  base_operating_kcad: number;
  one_time_operating_kcad: number;
  capital_kcad: number;
};

type RowMeta = {
  order: number;
  description: string;
  baseMcad: number;
  oneTimeMcad: number;
  capitalMcad: number;
};

export function ClimateFocusAreasTreemap({
  focusAreas,
  spend,
}: {
  focusAreas: FocusAreaRow[];
  spend: FocusAreaSpendRow[];
}) {
  const spendDetailByArea = new Map<
    string,
    { total: number; base: number; oneTime: number; capital: number }
  >(
    spend.map((r) => {
      const base = r.base_operating_kcad / 1000;
      const oneTime = r.one_time_operating_kcad / 1000;
      const capital = r.capital_kcad / 1000;
      return [
        r.focus_area,
        { total: base + oneTime + capital, base, oneTime, capital },
      ];
    }),
  );
  const spendByArea = new Map<string, number>(
    [...spendDetailByArea.entries()].map(([k, v]) => [k, v.total]),
  );

  // Rank-based shade assignment: smallest spend = lightest, biggest = darkest.
  // Driving the ramp by rank (not value) keeps shades evenly spaced even when
  // spend numbers cluster — Mobility's outlier scale otherwise crushes the
  // other eight into near-identical tints.
  const ranked = [...spendByArea.entries()]
    .sort((a, b) => a[1] - b[1])
    .map(([name], i, arr) => [name, arr.length === 1 ? 1 : i / (arr.length - 1)] as const);
  const rankT = new Map<string, number>(ranked);

  const rows: TreemapRow<RowMeta>[] = focusAreas.map((f) => {
    const detail = spendDetailByArea.get(f.name);
    return {
      label: f.name,
      value: detail?.total ?? 0,
      color: rampGreen(rankT.get(f.name) ?? 0.5),
      meta: {
        order: f.order,
        description: f.description,
        baseMcad: detail?.base ?? 0,
        oneTimeMcad: detail?.oneTime ?? 0,
        capitalMcad: detail?.capital ?? 0,
      },
    };
  });

  return (
    <Treemap
      data={rows}
      height={420}
      valueLabelFormatter={(v) => `$${v.toFixed(1)}M`}
      tooltipLabel="4-year spend"
      tooltipValueFormatter={(v) => `$${v.toFixed(2)}M`}
      tooltipDescription={(row) => {
        const m = row.meta;
        if (!m) return null;
        const total = row.value || 1;
        const fmt = (v: number) =>
          `$${v.toFixed(v < 1 ? 2 : 1)}M (${Math.round((v / total) * 100)}%)`;
        return (
          <>
            <div>{m.description}</div>
            <ul className="mt-1.5 list-none p-0 cs-mono text-xs leading-snug">
              <li>Capital · {fmt(m.capitalMcad)}</li>
              <li>One-time op · {fmt(m.oneTimeMcad)}</li>
              <li>Base op · {fmt(m.baseMcad)}</li>
            </ul>
          </>
        );
      }}
    />
  );
}
